'use client';

import { c as _c } from "react/compiler-runtime";
import type { QueryKey } from '@tanstack/query-core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLiveQuery } from '@tanstack/react-db';
import { useDbSessionState } from './db-session-provider.tsx';
import type { DbSession } from './db-session.ts';
import { createSelectedSessionCollection } from './session-collection.ts';
type Config<TItem extends object, TKey extends number | string> = {
  getKey: (_item: TItem) => TKey;
  id: string;
  queryFn: () => Promise<TItem[]>;
  queryKey: QueryKey;
  schemaVersion: number;
  staleTime?: number;
};
type OrderedItem<TItem extends object> = {
  position: number;
  value: TItem;
};
type Result<TItem extends object> = {
  data: TItem[] | undefined;
  error: unknown;
  isError: boolean;
  isFetching: boolean;
  isPending: boolean;
  refetch: () => Promise<void>;
};
type SessionCollection<TItem extends object, TKey extends number | string> = ReturnType<typeof createSelectedSessionCollection<OrderedItem<TItem>, TKey, TItem[]>>;
const collections = new WeakMap<DbSession, Map<string, unknown>>();
const getSessionCollection = <TItem extends object, TKey extends number | string>(session: DbSession, config: Config<TItem, TKey>, queryClient: ReturnType<typeof useQueryClient>): SessionCollection<TItem, TKey> => {
  let sessionCollections = collections.get(session);
  if (!sessionCollections) {
    sessionCollections = new Map();
    collections.set(session, sessionCollections);
  }
  const existing = sessionCollections.get(config.id);
  if (existing) {
    // SAFETY: this map is scoped to this collection ID and receives only this factory's collection.
    return existing as SessionCollection<TItem, TKey>;
  }

  // QueryClient is shared across account changes, while session collections are not. Discard a
  // prior session's raw response before this collection starts syncing so it cannot hydrate rows
  // into the next user's database.
  queryClient.removeQueries({
    exact: true,
    queryKey: config.queryKey
  });
  const collection = createSelectedSessionCollection<OrderedItem<TItem>, TKey, TItem[]>(session, {
    getKey: item => config.getKey(item.value),
    id: config.id,
    queryClient,
    queryFn: config.queryFn,
    queryKey: config.queryKey,
    schemaVersion: config.schemaVersion,
    select: rows => rows.map((value, position) => ({
      position,
      value
    })),
    staleTime: config.staleTime
  });
  sessionCollections.set(config.id, collection);
  return collection;
};

/**
 * Reads a REST-backed collection from the active database session when one exists. Query keeps
 * raw response rows, while the collection privately stores API order for persisted rendering.
 */
export const useDbSessionQueryCollection = config => {
  const $ = _c(37);
  const queryClient = useQueryClient();
  const {
    hasProvider,
    isOpening,
    session
  } = useDbSessionState();
  let t0;
  if ($[0] !== config || $[1] !== queryClient || $[2] !== session) {
    t0 = session ? getSessionCollection(session, config, queryClient) : undefined;
    $[0] = config;
    $[1] = queryClient;
    $[2] = session;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const collection = t0;
  const t1 = !hasProvider;
  let t2;
  if ($[4] !== config.queryFn || $[5] !== config.queryKey || $[6] !== config.staleTime || $[7] !== t1) {
    t2 = {
      enabled: t1,
      queryFn: config.queryFn,
      queryKey: config.queryKey,
      staleTime: config.staleTime
    };
    $[4] = config.queryFn;
    $[5] = config.queryKey;
    $[6] = config.staleTime;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const query = useQuery(t2);
  let t3;
  if ($[9] !== collection) {
    t3 = {
      query: q => collection && q.from({
        item: collection
      }).orderBy(_temp, "asc")
    };
    $[9] = collection;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const {
    data: orderedData
  } = useLiveQuery(t3);
  let t4;
  if ($[11] !== hasProvider || $[12] !== isOpening || $[13] !== query || $[14] !== session) {
    t4 = async () => {
      if (isOpening || hasProvider && !session) {
        return;
      }
      await query.refetch();
    };
    $[11] = hasProvider;
    $[12] = isOpening;
    $[13] = query;
    $[14] = session;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const refetch = t4;
  if (isOpening) {
    let t5;
    if ($[16] !== refetch) {
      t5 = {
        data: undefined,
        error: undefined,
        isError: false,
        isFetching: false,
        isPending: true,
        refetch
      };
      $[16] = refetch;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    return t5;
  }
  if (hasProvider && !collection) {
    let t5;
    if ($[18] !== refetch) {
      t5 = {
        data: undefined,
        error: undefined,
        isError: false,
        isFetching: false,
        isPending: false,
        refetch
      };
      $[18] = refetch;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    return t5;
  }
  if (!collection) {
    let t5;
    if ($[20] !== query.data || $[21] !== query.error || $[22] !== query.isError || $[23] !== query.isFetching || $[24] !== query.isPending || $[25] !== refetch) {
      t5 = {
        data: query.data,
        error: query.error,
        isError: query.isError,
        isFetching: query.isFetching,
        isPending: query.isPending,
        refetch
      };
      $[20] = query.data;
      $[21] = query.error;
      $[22] = query.isError;
      $[23] = query.isFetching;
      $[24] = query.isPending;
      $[25] = refetch;
      $[26] = t5;
    } else {
      t5 = $[26];
    }
    return t5;
  }
  const hasPersistedRows = !!orderedData?.length;
  const hasConfirmedEmpty = query.data?.length === 0 && !query.isPending;
  let data;
  if (hasPersistedRows) {
    let t5;
    if ($[27] !== orderedData) {
      t5 = orderedData.map(_temp2);
      $[27] = orderedData;
      $[28] = t5;
    } else {
      t5 = $[28];
    }
    data = t5;
  } else {
    if (hasConfirmedEmpty) {
      let t5;
      if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = [];
        $[29] = t5;
      } else {
        t5 = $[29];
      }
      data = t5;
    }
  }
  const t5 = data === undefined && !query.isError;
  let t6;
  if ($[30] !== data || $[31] !== query.error || $[32] !== query.isError || $[33] !== query.isFetching || $[34] !== refetch || $[35] !== t5) {
    t6 = {
      data,
      error: query.error,
      isError: query.isError,
      isFetching: query.isFetching,
      isPending: t5,
      refetch
    };
    $[30] = data;
    $[31] = query.error;
    $[32] = query.isError;
    $[33] = query.isFetching;
    $[34] = refetch;
    $[35] = t5;
    $[36] = t6;
  } else {
    t6 = $[36];
  }
  return t6;
};
function _temp(t0) {
  const {
    item
  } = t0;
  return item.position;
}
function _temp2(item_0) {
  return item_0.value;
}